<template lang="pug">
.send-message-reply-detail-box.d-flex.flex-column.align-items-stretch
  .message-list-title
    span {{ _title }}
  .message-list.d-flex.flex-column
    .message-content.d-flex.flex-column(v-BottomScroll)
      .message(v-for="message in _message")
        .line-left(v-if="message.senderType == 1")
          .text
            template(v-for="content in createSendMessageContents(message.message)")
              .text-content.pre-line(v-if="content.type == 'text'")
                span {{ content.value }}
              .link-content.text-center(v-else style="padding: 4px;")
                a(:href="content.uri" target="_blank") {{ content.value }}
          .date {{ convertDate(message.datetime) }}
        .line-right(v-else)
          .text.pre-line {{ message.message }}
          .date {{ convertDate(message.datetime) }}
    .reply-content
      .reply.d-flex.flex-wrap.align-items-stretch
        .reply-input
          textarea(v-model="_value" name="reply-message" placeholder="返信内容を入力"
            v-validate="'required|max:1000'" data-vv-as="返信内容" maxlength="1000" rows="10" max-rows="10"
            :disabled="getDeleted(message)" :class="{ 'disable': getDeleted(message) }")
        .buttons.d-flex.flex-wrap.flex-column
          .d-flex.ml-2
            HelpIcon(popoverId="sendmessage-reply-detail-modal-message-url-help" title="リンクの記載について")
              template(v-slot:text)
                span メッセージ内でURLリンクを使用したい場合は、改行してURLを記載することでリンクを送ることができます。
                br
                br
                strong 記載例
                br
                span 以下のリンクからコニカミノルタへアクセスしてください。
                br
                span https://www.konicaminolta.com
                br
                br
                strong リンク名を付ける方法
                br
                span 以下のようにリンク名とURLを記載すると、リンク名を表示することができます。
                br
                span [リンク名](URL)
                br
                br
                strong 記載例（リンク名を付ける方法）
                br
                span [HitomeQ](https://konicaminolta.com)
          .d-flex.flex-row.flex-fill
            button.btn.reply-btn.ml-2.align-self-center(@click="onReply" :disabled="disableUseSendAndReply(message)"
              :class="{ 'disable': disableUseSendAndReply(message) }") 送信
            button.btn.back-btn.ml-2.align-self-center(@click="onBack") 戻る
          .error-messages.ml-2
            label.error-message {{ errors.first('reply-message') }}
          .error-messages.ml-2(v-if="getDeleted(message) == true")
            label.error-message ユーザーが削除されているため返信できません
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";
import { userStore } from "@/store/store";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import BottomScroll from "@/pc/basics/BottomScroll";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import { SendMessageReplyListTableItem } from "@/pc/containers/sendMessages/SendMessageReplyListTable.vue";

/**
 * ### メッセージ送信の返信詳細
 */
@Component({
  components: {
    HelpIcon,
    FormTextarea
  },
  directives: {
    BottomScroll
  }
})
export default class SendMessageReplyDetailBox extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  @Prop()
  value!: string;

  @Prop()
  message!: SendMessageReplyListTableItem;

  get _value(): string {
    return this.value;
  }

  set _value(newValue: string) {
    this.$emit("input", newValue);
    return;
  }

  get _title(): string {
    if (this.message) {
      return `${this.message.mainName}（${this.message.subName}）`;
    } else {
      return "";
    }
  }

  get _message(): Array<any> {
    if (this.message) {
      return this.message.messages;
    } else {
      return [];
    }
  }

  public disableUseSendAndReply(message: any): boolean {
    if (message == null) {
      return false;
    }
    if (message.isDeleted == true) {
      return true;
    } else if (!userStore.loginUser.permissions.sendMessagesFunction.useSendAndReply) {
      return true;
    } else {
      return false;
    }
  }

  public getDeleted(message: any): boolean {
    if (message == null) {
      return false;
    }
    if (message.isDeleted == true) {
      return true;
    } else {
      return false;
    }
  }

  public convertDate(datetime: number) {
    const convertedDate: string = this.$dateUtil.convertNumberToString(datetime, "yyyy/mm/dd hh:MM");
    return convertedDate;
  }

  /**
   * ### 返信ボタン押下イベント
   */
  public async onReply(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.$emit("onReply", this.message);
    }

    return;
  }

  public onBack(): void {
    this.$validator.errors.remove("reply-message");
    this.$emit("onBack");
    return;
  }

  public createSendMessageContents(content: string): Array<{ type: string; value: string; uri?: string }> {
    // contentが存在しない場合は空配列を返す
    if (!content) {
      return [];
    }

    // template側でlinkボタンとテキストを判別するためにtypeを持たせる
    let contents: Array<{ type: string; value: string; uri?: string }> = [];
    const splitedContent: string[] = content.split("\n");
    let buttonCount: number = 1;

    for (const message of splitedContent) {
      let content: { type: string; value: string; uri?: string };

      // リンクボタンメッセージ(ボタン名指定)
      // 先頭に半角全角スペースがあるだけの場合は無視して扱い、それ以外はテキスト文字列として扱う
      if (message.match(/^\[(.*)\]\((.*)\)$/)) {
        const linkRegex = /\[(.*?)\]\((.*?)\)/g;
        const links: Array<{ name: string; url: string }> = [];
        let match: any;

        while ((match = linkRegex.exec(message)) !== null) {
          const name = match[1] ? match[1] : null;
          const url = match[2] ? match[2] : null;
          links.push({ name, url });
        }

        if (links.length == 1 && links[0].name != null && links[0].url != null) {
          content = {
            type: "link",
            value: `${links[0].name}`,
            uri: links[0].url
          };
          contents.push(content);
        } else {
          content = {
            type: "text",
            value: message
          };
          contents.push(content);
        }
      }
      // リンクボタンメッセージ(ボタン名指定無し)
      // 先頭に半角全角スペースがあるだけの場合はURLとして扱い、それ以外はテキスト文字列として扱う
      else if (message.match(/^\s*(http|https):\/\/.+/)) {
        // 前後の半角全角スペースを取り除き、URLデコード&エンコードを考慮したURIを作成する
        const uri: string = message.replace(/^\s+|\s+$/g, "");
        const decodedUri: string = decodeURI(uri);
        const encodedUri: string = encodeURI(decodedUri);

        content = {
          type: "link",
          value: `リンク その${buttonCount}`,
          uri: encodedUri
        };
        buttonCount++;
        contents.push(content);
      }
      // テキストメッセージ
      else {
        const convertedMessage: string = message == "" ? "　" : message;
        content = {
          type: "text",
          value: convertedMessage
        };
        contents.push(content);
      }
    }

    return contents;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.send-message-reply-detail-box {
  border-top: solid 1px $border-darkgray;
  border-right: solid 1px $border-darkgray;
  border-bottom: solid 1px $border-darkgray;

  height: calc(100vh - 245px);

  .message-list-title {
    background-color: $bg-header;
    padding: 5px;
    min-height: 37px;
    font-weight: bold;
    text-align: center;
    border-bottom: solid 2px $border-lightgray;
  }
  .message-list {
    overflow-y: auto;
    overflow-x: auto;

    .message-content {
      overflow-y: scroll;
      overflow-x: auto;
      // 縦幅の自動調整。画面全体の大きさからユーザーリストの高さ以外を減算する
      height: calc(100vh - 466px);
      min-height: 300px;
      max-height: calc(100vh - 466px);
      position: relative;
      // 背景色
      background-color: $bg-message;

      .message {
        padding: 10px 0 10px 0;
        // botの発言
        .line-left {
          position: relative;
          display: inline-block;
          margin-bottom: 5px;
          clear: both;
          margin-left: 30px;
          max-width: 740px;
          // 発言本文
          .text {
            padding: 10px;
            border-radius: 20px;
            background-color: $bg-message-bot;
            margin: 0;
            margin-right: 80px;
            // LINEの吹き出しのように見せるために、CSSで図形を描画
            &::after {
              content: "";
              position: absolute;
              display: block;
              width: 0;
              height: 0;
              left: -10px;
              top: 10px;
              border-right: 20px solid $bg-message-bot;
              border-top: 10px solid transparent;
              border-bottom: 10px solid transparent;
            }
          }
          // 日付部分
          .date {
            color: $date-font-color;
            text-align: left;
            font-size: 14px;
            font-weight: bold;
          }
        }
        // 自分の発言
        .line-right {
          position: relative;
          margin-top: 5px;
          float: right;
          clear: both;
          margin-right: 30px;
          max-width: 740px;
          // 発言本文
          .text {
            padding: 10px;
            border-radius: 20px;
            background-color: $bg-message-mine;
            color: rgba(255, 255, 255, 1);
            margin: 0;
            margin-left: 80px;
            // LINEの吹き出しのように見せるために、CSSで図形を描画
            &::after {
              content: "";
              position: absolute;
              display: block;
              width: 0;
              height: 0;
              right: -10px;
              top: 10px;
              border-left: 20px solid $bg-message-mine;
              border-top: 10px solid transparent;
              border-bottom: 10px solid transparent;
            }
          }
          // 日付部分
          .date {
            color: $date-font-color;
            text-align: right;
            font-size: 14px;
            font-weight: bold;
          }
        }
      }
    }
    .reply-content {
      height: 182px;
      padding: 8px;
      .reply-input {
        flex-grow: 2;
        textarea {
          height: 152px;
          width: 100%;
          // デザインが崩れるのでリサイズは無効にする
          resize: none;
          @include font(16px);
          background-image: none;
          background-color: $bg-default;
          border: 1px solid $form-border-color;
          border-radius: 0.25rem;
          &.disable {
            cursor: not-allowed;
            background-color: $btn-disable !important;
          }
        }
      }
      // 入力欄にフォーカスが当たった際は強調する
      input:focus,
      textarea:focus,
      select:focus {
        // chromeが自動的に付与するスタイルを無効化する
        outline: none;
        box-shadow: 0 0 0 0.2rem $form-boxshadow-select-color;
        border: 1px solid $form-border-select-color !important;
        border-radius: 0.25rem;
      }
      .error-messages {
        flex-grow: 2;
        height: 27px;
      }
      .buttons {
        min-width: 132px;
        height: 162px;
      }
    }
  }
}
</style>
